import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import BorderCard from "../components/BorderCard";
import CardTitle from "../components/CardTitle";
import StepsCard from "../components/StepsCard";
import ConditionCard from "../components/ConditionCard";
import TimePeriodPill from "../components/TimePeriodPill";
import StartNode from "../components/StartNode";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import {
  useDeleteIndividualSequenceStep,
  useUpdateCampaignStep,
} from "@/features/campaigns/hooks/useCampaign";
import { useSequenceContentLoaderState } from "@/features/campaigns/hooks/useSequenceContentLoaderState";
import StepConditionModal from "../Sequence/StepConditionModal";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import Stars from "@/assets/images/Stars.png";
import { useMainStore } from "@/stores/index";
import AiIcon from "@/assets/svg/Ai-Icon";
import CampaignContentGeneratingLoader from "./CampaignContentGeneratingLoader";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import InlineAlert from "@/shared/components/ui/Alert/InlineAlert";

const IndividualSequenceContentArea = ({
  content,
  sequenceContent,
  isLoading,
  isFetching = false,
  isUpdating,
  currentLead,
  showTrainerUpdatePrompt = false,
  onTrainerMemoryUpdate,
  isTrainerMemoryUpdating = false,
}) => {
  const queryClient = useQueryClient();
  const campaignId = useCampaignId();
  const [showAddModal, setShowAddModal] = useState(false);
  const [stepId, setStepId] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const workflowMode = useMainStore((state) => state.workflowMode);
  const isSmartMode = workflowMode === "smart";

  const prospectId = currentLead?.contact_id ?? currentLead?.id;

  const { view, progressSnapshot, isGeneratingActive, completeGeneratingSurface } =
    useSequenceContentLoaderState({
      sequenceContent,
      isLoading,
      isFetching,
      isUpdating,
      prospectId,
    });

  const { mutate: deleteIndividualSequenceStep } =
    useDeleteIndividualSequenceStep({
      action: () => {
        queryClient.invalidateQueries({
          queryKey: [
            "campaign-individual-sequencing-content",
            campaignId,
            prospectId,
          ],
        });
      },
    });

  const { mutate: updateCampaignStep, isPending: isUpdatingCondition } =
    useUpdateCampaignStep();
  const contentGeneration = sequenceContent?.contentGeneration;
  const isMissingContent = sequenceContent?.isContentGenerated === false;
  const needsFixedMessages = contentGeneration === "off" && isMissingContent;

  const handleDeleteStep = (step) => {
    deleteIndividualSequenceStep({
      stepId: step.id,
      enrollmentId: currentLead?.id,
    });
  };

  const handleEditCondition = (step) => {
    setStepId(step?.id);
    setShowAddModal(true);
  };

  const handleDeleteCondition = (step) => {
    setStepId(step?.id);
    setShowDeleteConfirmModal(true);
  };

  const handleSaveOrDeleteCondition = (data) => {
    if (!stepId) return;
    const conditions =
      data?.condition?.text != null
        ? JSON.stringify(data.condition.text)
        : null;

    updateCampaignStep(
      {
        campaignId,
        stepId,
        stepData: { conditions },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [
              "campaign-individual-sequencing-content",
              campaignId,
              prospectId,
            ],
          });
          setShowAddModal(false);
          setShowDeleteConfirmModal(false);
          setStepId(null);
        },
      },
    );
  };

  return (
    <>
      <div className="w-[66%] relative">
        <BorderCard>
          <div className="flex gap-2.5 items-center justify-between w-full">
            <CardTitle text="Campaign Sequence" />
            <div className="flex gap-2.5 items-center flex-wrap justify-end">
              {showTrainerUpdatePrompt && (
                <CustomCommonTooltip
                  content="Your memories were updated. Click to rewrite this lead's AI messages with them. Messages you edited by hand are kept."
                  className="max-w-[320px]"
                  placement="bottom-end"
                >
                  <button
                    type="button"
                    onClick={onTrainerMemoryUpdate}
                    disabled={isTrainerMemoryUpdating}
                    className="inline-flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3.5 py-2 text-[0.685rem] font-semibold text-amber-700 dark:text-amber-300 transition-colors hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label="Update content with the latest AI Trainer memory"
                  >
                    <AiIcon />
                    {isTrainerMemoryUpdating
                      ? "Updating content..."
                      : "New update available"}
                  </button>
                </CustomCommonTooltip>
              )}
              {isSmartMode && (
                <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3.5 py-2.5">
                  <img
                    src={Stars}
                    alt="dotted"
                    className=" object-contain size-3"
                  />
                  <p className="text-[0.685rem] font-medium text-gray-800">
                    Your Sequence has been modified by AI
                  </p>
                </div>
              )}
            </div>
          </div>
        </BorderCard>

        <BorderCard
          variant="bottom"
          className="h-[calc(100dvh-195px)] overflow-y-auto scrollbar-hide"
          bg={true}
        >
          <div className="px-5 flex flex-col w-full items-center relative">
            {needsFixedMessages && (
              <InlineAlert variant="warning" title="Some steps have no message yet" className="mt-4 w-full">
                AI is off for this campaign. Add a fixed message to those steps in Campaign Steps, or a campaign message for their channel in Settings.
              </InlineAlert>
            )}
            {view === "normal" ? (
              <Loader
                text={
                  isUpdating
                    ? "Updating campaign steps content..."
                    : "Loading campaign steps content..."
                }
              />
            ) : view === "generating" ? (
              <CampaignContentGeneratingLoader
                key={prospectId}
                isGenerating={isGeneratingActive}
                contentDone={progressSnapshot.contentDone}
                contentTotal={progressSnapshot.total}
                percent={progressSnapshot.percent}
                onComplete={completeGeneratingSurface}
              />
            ) : (
              <>
                <StartNode />
                {content?.map((step, index) => (
                  <div
                    key={index}
                    className={`flex flex-col w-full items-center relative ${index === content?.length - 1 ? "pb-3" : ""
                      }`}
                  >
                    <div className="w-px h-8 border-l border-dashed border-gray-400 dark:border-gray-500" />

                    {step?.conditions && (
                      <div>
                        <ConditionCard
                          condition={{
                            conditions: step?.conditions,
                            duration: step?.duration,
                          }}
                          onEdit={() => handleEditCondition(step)}
                          onDelete={() => handleDeleteCondition(step)}
                        />

                        {showAddModal && (
                          <StepConditionModal
                            isOpen={showAddModal}
                            onClose={() => setShowAddModal(false)}
                            data={step}
                            onSave={handleSaveOrDeleteCondition}
                            step={step}
                            condition={{
                              conditions: step?.conditions,
                            }}
                            isUpdatingStep={isUpdatingCondition}
                          />
                        )}
                      </div>
                    )}

                    {step?.step_order > 1 && (
                      <div className="w-px h-6 border-l border-dashed border-gray-400 dark:border-gray-500" />
                    )}

                    <div>
                      {step?.step_order > 1 && (
                        <TimePeriodPill duration={step?.duration} />
                      )}
                    </div>

                    {step?.conditions?.length !== 0 && (
                      <div className="w-px h-8 border-l border-dashed border-gray-400 dark:border-gray-500" />
                    )}
                    <StepsCard
                      step={step}
                      stepOrder={step.step_order}
                      onDelete={handleDeleteStep}
                      prospectId={prospectId}
                      enrollmentId={currentLead?.id}
                      onRetryContent={() =>
                        // Re-fetching asks the server to regenerate missing content.
                        queryClient.invalidateQueries({
                          queryKey: [
                            "campaign-individual-sequencing-content",
                            campaignId,
                            prospectId,
                          ],
                        })
                      }
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </BorderCard>
      </div>

      {showDeleteConfirmModal && (
        <DeleteConfirmationModal
          title="Are you sure you want to delete this condition?"
          description="Deleting this condition will permanently erase your condition - This action can’t be undone."
          showConfirmDelete={showDeleteConfirmModal}
          onClose={() => setShowDeleteConfirmModal(false)}
          onDelete={() => handleSaveOrDeleteCondition()}
          deleteButtonText="Yes, delete condition"
          cancelButtonText="I changed my mind"
        />
      )}
    </>
  );
};

export default IndividualSequenceContentArea;
